import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type {
  ColDef,
  GridReadyEvent,
  RowClickedEvent,
} from "ag-grid-community";
import {
  usePaymentVoucher,
  usePaymentVoucherStore,
} from "hooks";
import {
  PaymentVoucherItem,
  PaymentVoucherForm,
  PaymentVoucherQuery,
  PaymentVoucherRow,
} from "types";
import SnackbarUtils from "utils/SnackbarUtils";
import { AgGridTable, ConfirmDialog, LoadingState } from "components";
import type { QueryState } from "components/ui-common/AgGridTable";
import { Plus } from "lucide-react";

const todayString = () => new Date().toISOString().slice(0, 10);

const PaymentVouchers: React.FC = () => {
  const gridApiRef = useRef<GridReadyEvent["api"] | null>(null);
  const addRowRef = useRef<PaymentVoucherRow | null>(null);
  const [addRowVersion, setAddRowVersion] = useState(0);
  const [queryParams, setQueryParams] = useState<PaymentVoucherQuery>({
    page: 1,
    pageSize: 20,
  });
  const [selectedVoucherId, setSelectedVoucherId] = useState<
    number | string | "pinned" | null
  >(null);

  const [itemDrafts, setItemDrafts] = useState<PaymentVoucherItem[]>([]);
  const [showInlineAddRow, setShowInlineAddRow] = useState<any>("");
  const [confirmRow, setConfirmRow] = useState<PaymentVoucherRow | null>(null);
  const {
    rows,
    setRows,
    upsertRow,
    removeRow,
    editingId,
    setEditingId,
    isLoading,
    setLoading,
  } = usePaymentVoucherStore();
  const selectedVoucher = useMemo(() => {
    if (selectedVoucherId === "pinned") {
      return addRowRef.current ?? null;
    }
    if (!rows.length || selectedVoucherId === null) return null;
    return (
      rows.find((row) => String(row.id) === String(selectedVoucherId)) ?? null
    );
  }, [rows, selectedVoucherId]);
  const {
    fetchList,
    createPaymentVoucher,
    updatePaymentVoucher,
    deletePaymentVoucher,
  } = usePaymentVoucher();

  const {
    data: paymentVoucherData,
    isFetching,
    refetch,
  } = fetchList(queryParams, true);

  const parseNumber = useCallback((value: unknown) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === "number" && Number.isFinite(value)) return value;
    const parsed = Number(String(value).replace(/,/g, ""));
    return Number.isFinite(parsed) ? parsed : 0;
  }, []);

  const normalizeAttachments = useCallback((value: unknown): string[] => {
    if (Array.isArray(value)) {
      return value
        .map((item) => (typeof item === "string" ? item : String(item ?? "")))
        .map((item) => item.trim())
        .filter(Boolean);
    }

    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return [];

      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed
            .map((item) => (typeof item === "string" ? item : String(item ?? "")))
            .map((item) => item.trim())
            .filter(Boolean);
        }
      } catch (error) {
        // ignore invalid JSON strings
      }

      return trimmed
        .split(/[,\\n]/)
        .map((item) => item.trim())
        .filter(Boolean);
    }

    return [];
  }, []);

  const sanitizeItems = useCallback(
    (items?: PaymentVoucherItem[]) =>
      Array.isArray(items)
        ? items
          .map((item) => ({
            amount: parseNumber(item.amount),
            partner_id:
              item.partner_id !== undefined &&
                item.partner_id !== null &&
                item.partner_id !== ""
                ? Number(item.partner_id)
                : undefined,
            reason: item.reason ? String(item.reason).trim() : undefined,
            description: item.description
              ? String(item.description).trim()
              : undefined,
          }))
          .filter(
            (item) =>
              item.amount ||
              item.partner_id ||
              Boolean(item.reason) ||
              Boolean(item.description),
          )
        : [],
    [parseNumber],
  );

  const withItemFields = useCallback(
    (voucher: PaymentVoucherRow): PaymentVoucherRow => {
      const normalizedItems = sanitizeItems(voucher.items);
      const firstItem = normalizedItems[0];
      return {
        ...voucher,
        attachments: normalizeAttachments(voucher.attachments),
        items: normalizedItems,
        amount:
          voucher.amount ??
          (typeof firstItem?.amount === "number"
            ? firstItem.amount
            : firstItem?.amount
              ? Number(firstItem.amount)
              : undefined),
        partner_id: voucher.partner_id ?? firstItem?.partner_id,
        reason: voucher.reason ?? firstItem?.reason,
        item_description: voucher.item_description ?? firstItem?.description,
      };
    },
    [normalizeAttachments, sanitizeItems],
  );

  useEffect(() => {
    if (paymentVoucherData?.data) {
      const mapped =
        (paymentVoucherData.data as PaymentVoucherRow[])?.map((item) =>
          withItemFields(item),
        ) ?? [];
      setRows(mapped);
    }
  }, [paymentVoucherData, setRows, withItemFields]);

  useEffect(() => {
    if (selectedVoucherId === "pinned") return;

    if (!rows.length) {
      setSelectedVoucherId(null);
      return;
    }

    if (selectedVoucherId === null) return;

    const exists = rows.some(
      (row) => String(row.id) === String(selectedVoucherId),
    );

    if (!exists) {
      setSelectedVoucherId(null);
    }
  }, [rows, selectedVoucherId]);

  useEffect(() => {
    setItemDrafts(selectedVoucher ? sanitizeItems(selectedVoucher.items) : []);
  }, [sanitizeItems, selectedVoucher]);

  useEffect(() => {
    setLoading(
      Boolean(
        isFetching ||
        createPaymentVoucher.isPending ||
        updatePaymentVoucher.isPending ||
        deletePaymentVoucher.isPending,
      ),
    );
  }, [
    isFetching,
    createPaymentVoucher.isPending,
    updatePaymentVoucher.isPending,
    deletePaymentVoucher.isPending,
    setLoading,
  ]);

  const columns = useMemo<ColDef<PaymentVoucherRow>[]>(() => {
    return [
      {
        headerName: "Ngày chi",
        field: "payment_date",
        minWidth: 140,
        filter: "agDateColumnFilter",
      },
      {
        headerName: "Số phiếu",
        field: "original_voucher_no",
        colId: "code",
        minWidth: 140,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Đối tượng",
        field: "object_name",
        minWidth: 180,
        flex: 1,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Người nhận",
        field: "receiver_name",
        minWidth: 180,
        flex: 1,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Địa chỉ",
        field: "address",
        minWidth: 200,
        flex: 1,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Mô tả",
        field: "description",
        minWidth: 200,
        flex: 1,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Tệp đính kèm",
        field: "attachments",
        minWidth: 180,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Hóa đơn nhập",
        field: "import_invoice_id",
        minWidth: 150,
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Số tiền",
        field: "amount",
        minWidth: 160,
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Mã đối tác",
        field: "partner_id",
        minWidth: 140,
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Lý do chi",
        field: "reason",
        minWidth: 180,
        flex: 1,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Mô tả chi tiết",
        field: "item_description",
        minWidth: 200,
        flex: 1,
        filter: "agTextColumnFilter",
      },
    ];
  }, []);

  const normalizePayload = useCallback(
    (row: Partial<PaymentVoucherRow>): PaymentVoucherForm => {
      const normalizedItems = sanitizeItems(row.items);
      const firstItem = normalizedItems[0];

      const amount = parseNumber(
        row.amount ?? firstItem?.amount ?? row.items?.[0]?.amount,
      );
      const partnerId =
        row.partner_id ??
        firstItem?.partner_id ??
        row.items?.[0]?.partner_id ??
        undefined;
      const reason =
        row.reason ?? firstItem?.reason ?? row.items?.[0]?.reason ?? "";
      const detail =
        row.item_description ??
        firstItem?.description ??
        row.items?.[0]?.description ??
        "";
      const attachments = normalizeAttachments(row.attachments);

      const importInvoice =
        row.import_invoice_id !== undefined &&
          row.import_invoice_id !== null &&
          row.import_invoice_id !== ""
          ? Number(row.import_invoice_id)
          : undefined;

      const items =
        normalizedItems.length > 0
          ? normalizedItems
          : amount || partnerId || reason || detail
            ? [
              {
                amount,
                partner_id: partnerId ? Number(partnerId) : undefined,
                reason: reason ? String(reason).trim() : undefined,
                description: detail ? String(detail).trim() : undefined,
              },
            ]
            : [];

      return {
        payment_date: String(row.payment_date ?? "").trim(),
        object_name: String(row.object_name ?? "").trim(),
        receiver_name: String(row.receiver_name ?? "").trim(),
        address: row.address ? String(row.address).trim() : "",
        description: row.description ? String(row.description).trim() : "",
        attachments,
        original_voucher_no: row.original_voucher_no
          ? String(row.original_voucher_no).trim()
          : "",
        import_invoice_id: importInvoice,
        items,
      };
    },
    [normalizeAttachments, parseNumber, sanitizeItems],
  );

  type PersistMeta = {
    pinned?: boolean;
    onRevert?: () => void;
    onSuccess?: () => void;
    refetch?: boolean;
  };

  const resetInlineAddRow = useCallback(() => {
    addRowRef.current = {
      payment_date: todayString(),
      object_name: "",
      receiver_name: "",
      address: "",
      description: "",
      attachments: [],
      original_voucher_no: "",
      import_invoice_id: undefined,
      amount: undefined,
      partner_id: undefined,
      reason: "",
      item_description: "",
      items: [],
    } as PaymentVoucherRow;
    setAddRowVersion((v) => v + 1);
  }, []);

  const persistRow = useCallback(
    (row: Partial<PaymentVoucherRow>, meta?: PersistMeta) => {
      const payload = normalizePayload(row);

      if (
        !payload.payment_date ||
        !payload.object_name ||
        !payload.receiver_name
      ) {
        SnackbarUtils.error(
          "Ngày chi, Đối tượng và Người nhận là bắt buộc",
        );
        meta?.onRevert?.();
        return;
      }

      if (!payload.items?.length || !payload.items[0].amount) {
        SnackbarUtils.error("Vui lòng nhập số tiền chi");
        meta?.onRevert?.();
        return;
      }

      const shouldCreate = meta?.pinned || !row.id;
      if (shouldCreate) {
        createPaymentVoucher.mutate(payload, {
          onSuccess: (res) => {
            if (res?.data) {
              upsertRow(withItemFields(res.data as PaymentVoucherRow));
            }
            resetInlineAddRow();
            if (meta?.refetch !== false) {
              refetch();
            }
            meta?.onSuccess?.();
          },
          onError: () => {
            SnackbarUtils.error("Thêm phiếu chi không thành công");
            meta?.onRevert?.();
          },
        });
        return;
      }

      if (!row.id) {
        SnackbarUtils.error("Không xác định được phiếu chi cần cập nhật");
        meta?.onRevert?.();
        return;
      }

      updatePaymentVoucher.mutate(
        { id: row.id, ...payload },
        {
          onSuccess: (res) => {
            if (res?.data) {
              upsertRow(withItemFields(res.data as PaymentVoucherRow));
            } else {
              upsertRow(
                withItemFields({ ...(row as PaymentVoucherRow), ...payload }),
              );
            }
            if (meta?.refetch) {
              refetch();
            }
            meta?.onSuccess?.();
          },
          onError: () => {
            SnackbarUtils.error("Cập nhật phiếu chi không thành công");
            meta?.onRevert?.();
          },
        },
      );
    },
    [
      createPaymentVoucher,
      normalizePayload,
      refetch,
      resetInlineAddRow,
      updatePaymentVoucher,
      upsertRow,
      withItemFields,
    ],
  );

  const handleSaveRow = useCallback(
    (row: PaymentVoucherRow, meta?: { pinned?: boolean }) => {
      persistRow(row, {
        pinned: meta?.pinned,
        refetch: meta?.pinned || !row.id,
        onSuccess: () => {
          setShowInlineAddRow("");
          setEditingId(null);
        },
      });
    },
    [persistRow, setEditingId],
  );

  const handleEditRow = useCallback(
    (params) => {
      setShowInlineAddRow("edit");
      setEditingId(params.data.id);
    },
    [setEditingId],
  );

  const handleRowClick = useCallback((event: RowClickedEvent<PaymentVoucherRow>) => {
    if (event.node?.rowPinned) {
      setSelectedVoucherId("pinned");
      return;
    }
    setSelectedVoucherId(event.data?.id ?? null);
  }, []);

  const handleDeleteRow = useCallback((row: PaymentVoucherRow) => {
    setConfirmRow(row);
    setShowInlineAddRow("");
  }, []);

  const handleAddItem = useCallback(() => {
    if (!selectedVoucher && selectedVoucherId !== "pinned") return;
    setItemDrafts((prev) => [
      ...prev,
      { amount: 0, partner_id: "", reason: "", description: "" },
    ]);
  }, [selectedVoucher, selectedVoucherId]);

  const handleUpdateItem = useCallback(
    (
      index: number,
      key: keyof PaymentVoucherItem,
      value: string | number,
    ) => {
      setItemDrafts((prev) =>
        prev.map((item, i) =>
          i === index
            ? {
                ...item,
                [key]: key === "amount" ? parseNumber(value) : value,
              }
            : item,
        ),
      );
    },
    [parseNumber],
  );

  const handleRemoveItem = useCallback((index: number) => {
    setItemDrafts((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSaveItems = useCallback(() => {
    if (!selectedVoucher && selectedVoucherId !== "pinned") return;
    const normalizedItems = sanitizeItems(itemDrafts);

    if (!normalizedItems.length) {
      SnackbarUtils.error("Vui lòng thêm ít nhất 1 dòng chi tiết");
      return;
    }

    if (selectedVoucherId === "pinned") {
      const baseDraft =
        addRowRef.current ??
        ({
          payment_date: todayString(),
          object_name: "",
          receiver_name: "",
          address: "",
          description: "",
          attachments: [],
          original_voucher_no: "",
          import_invoice_id: undefined,
          amount: undefined,
          partner_id: undefined,
          reason: "",
          item_description: "",
        } as PaymentVoucherRow);

      const mergedDraft = withItemFields({
        ...baseDraft,
        items: normalizedItems,
      });
      addRowRef.current = mergedDraft;
      setItemDrafts(normalizedItems);
      SnackbarUtils.success("Đã lưu chi tiết cho phiếu chi mới");
      return;
    }

    if (!selectedVoucher?.id) return;

    const mergedRow = withItemFields({
      ...selectedVoucher,
      items: normalizedItems,
    } as PaymentVoucherRow);

    const payload = normalizePayload(mergedRow);

    updatePaymentVoucher.mutate(
      { id: selectedVoucher.id, ...payload },
      {
        onSuccess: (res) => {
          const next =
            res?.data && (res.data as PaymentVoucherRow)
              ? withItemFields(res.data as PaymentVoucherRow)
              : withItemFields(mergedRow);
          setSelectedVoucherId(next.id);
          upsertRow(next);
          SnackbarUtils.success("Đã lưu chi tiết phiếu chi");
        },
        onError: () => {
          SnackbarUtils.error("Lưu chi tiết phiếu chi không thành công");
        },
      },
    );
  }, [
    itemDrafts,
    normalizePayload,
    sanitizeItems,
    selectedVoucher,
    updatePaymentVoucher,
    upsertRow,
    withItemFields,
  ]);

  const handleConfirmDelete = useCallback(() => {
    if (!confirmRow) return;
    deletePaymentVoucher.mutate(confirmRow.id, {
      onSuccess: () => {
        removeRow(confirmRow.id);
        if (editingId === confirmRow.id) {
          setEditingId(null);
        }
        setConfirmRow(null);
      },
      onError: () => {
        SnackbarUtils.error("Xóa phiếu chi không thành công");
      },
    });
  }, [
    confirmRow,
    deletePaymentVoucher,
    editingId,
    removeRow,
    setEditingId,
  ]);

  const toggleInlineAddRow = useCallback(() => {
    setEditingId(null);
    gridApiRef.current?.stopEditing();
    setShowInlineAddRow((prev) => {
      const next = !prev ? "add" : "";
      if (next) {
        resetInlineAddRow();
        setSelectedVoucherId("pinned");
        setItemDrafts(sanitizeItems(addRowRef.current?.items));
      } else {
        addRowRef.current = null;
        setAddRowVersion((v) => v + 1);
        if (selectedVoucherId === "pinned") {
          setSelectedVoucherId(null);
          setItemDrafts([]);
        }
      }
      return next;
    });
  }, [resetInlineAddRow, sanitizeItems, selectedVoucherId, setEditingId]);

  const handleQueryChange = useCallback(
    (query: QueryState) => {
      setQueryParams((prev) => {
        const next = {
          ...prev,
          page: query.pagination?.page ?? prev.page,
          pageSize: query.pagination?.pageSize ?? prev.pageSize,
          sortModel: query.sortModel,
          filterModel: query.filterModel,
        };
        const unchanged =
          next.page === prev.page &&
          next.pageSize === prev.pageSize &&
          next.sortModel === prev.sortModel &&
          next.filterModel === prev.filterModel;
        return unchanged ? prev : next;
      });
    },
    [setQueryParams],
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      resizable: true,
      editable: (params) =>
        params.data?.id === editingId || params.node.rowPinned === "top",
      flex: 1,
      minWidth: 140,
    }),
    [editingId],
  );

  const inlineAddRowData = useMemo(
    () => (showInlineAddRow ? (addRowRef.current ?? undefined) : undefined),
    [showInlineAddRow, addRowVersion],
  );

  const totalDraftAmount = useMemo(
    () =>
      itemDrafts.reduce(
        (sum, item) => sum + (Number(item.amount ?? 0) || 0),
        0,
      ),
    [itemDrafts],
  );

  return (
    <div className="p-6 space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-gray-900">
          [Accounting] Payment Vouchers
        </h1>
        <p className="text-sm text-gray-600">
          Quản lý phiếu chi: tạo mới, chỉnh sửa nhanh trên bảng và xoá khi cần.
        </p>
      </div>

      <div className="relative bg-white border border-gray-200 shadow-sm rounded-lg p-4 overflow-hidden">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              Danh sách phiếu chi
            </h2>
            <p className="text-xs text-gray-500">
              Sử dụng grid để lọc, sắp xếp và chỉnh sửa trực tiếp.
            </p>
          </div>
        </div>
        <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-gray-900">
                {selectedVoucher
                  ? `Chi tiết phiếu chi${
                    selectedVoucher.original_voucher_no
                      ? ` #${selectedVoucher.original_voucher_no}`
                      : ""
                  }`
                  : "Chi tiết phiếu chi"}
              </p>
              <p className="text-xs text-gray-500">
                {selectedVoucher
                  ? `${selectedVoucher.object_name} • ${selectedVoucher.payment_date}`
                  : "Chưa chọn phiếu chi"}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleAddItem}
                disabled={!selectedVoucher && selectedVoucherId !== "pinned"}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-60"
              >
                Thêm dòng
              </button>
                <button
                  type="button"
                  onClick={handleSaveItems}
                  disabled={!selectedVoucher || updatePaymentVoucher.isPending || selectedVoucherId === "pinned"}
                  className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow hover:bg-blue-700 disabled:opacity-60"
                >
                  {updatePaymentVoucher.isPending ? "Đang lưu..." : "Lưu chi tiết"}
                </button>
              <div className="text-right">
                <p className="text-xs text-gray-500">Tổng tiền</p>
                <p className="text-sm font-semibold text-gray-900">
                  {totalDraftAmount.toLocaleString("vi-VN")}
                </p>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold">#</th>
                  <th className="px-4 py-2 text-left font-semibold">
                    Mã đối tác
                  </th>
                  <th className="px-4 py-2 text-left font-semibold">
                    Lý do chi
                  </th>
                  <th className="px-4 py-2 text-left font-semibold">
                    Mô tả
                  </th>
                  <th className="px-4 py-2 text-right font-semibold">
                    Số tiền
                  </th>
                  <th className="px-4 py-2 text-center font-semibold">
                    Hành động
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {itemDrafts.length ? (
                  itemDrafts.map((item, index) => (
                    <tr
                      key={`${selectedVoucher?.id ?? "draft"}-item-${index}`}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-4 py-2 text-gray-500">{index + 1}</td>
                      <td className="px-4 py-2 text-gray-900">
                        <input
                          value={item.partner_id ?? ""}
                          onChange={(e) =>
                            handleUpdateItem(index, "partner_id", e.target.value)
                          }
                          disabled={!selectedVoucher}
                          className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none disabled:opacity-60"
                          placeholder="Mã đối tác"
                        />
                      </td>
                      <td className="px-4 py-2 text-gray-900">
                        <input
                          value={item.reason ?? ""}
                          onChange={(e) =>
                            handleUpdateItem(index, "reason", e.target.value)
                          }
                          disabled={!selectedVoucher}
                          className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none disabled:opacity-60"
                          placeholder="Lý do chi"
                        />
                      </td>
                      <td className="px-4 py-2 text-gray-700">
                        <input
                          value={item.description ?? ""}
                          onChange={(e) =>
                            handleUpdateItem(index, "description", e.target.value)
                          }
                          disabled={!selectedVoucher}
                          className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none disabled:opacity-60"
                          placeholder="Mô tả"
                        />
                      </td>
                      <td className="px-4 py-2 text-right text-gray-900">
                        <input
                          type="number"
                          value={item.amount ?? 0}
                          onChange={(e) =>
                            handleUpdateItem(index, "amount", e.target.value)
                          }
                          disabled={!selectedVoucher}
                          className="w-full rounded border border-gray-200 px-2 py-1 text-sm text-right focus:border-blue-500 focus:outline-none disabled:opacity-60"
                          placeholder="0"
                        />
                      </td>
                      <td className="px-4 py-2 text-center">
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(index)}
                          disabled={!selectedVoucher}
                          className="text-red-600 hover:text-red-700 text-xs font-semibold disabled:opacity-60"
                        >
                          Xóa
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      className="px-4 py-4 text-center text-gray-500"
                      colSpan={6}
                    >
                      {selectedVoucher
                        ? 'Chưa có dòng chi tiết. Bấm "Thêm dòng" để bắt đầu.'
                        : "Chọn một phiếu chi để xem chi tiết."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="flex items-center justify-end mb-3">
          <button
            type="button"
            onClick={toggleInlineAddRow}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4 text-green-700" />
            {showInlineAddRow ? "Ẩn dòng thêm mới" : "Thêm phiếu chi"}
          </button>
        </div>
        <AgGridTable
          rows={rows}
          columns={columns}
          height={520}
          autoHeight={false}
          showAddRow={showInlineAddRow}
          addRowData={inlineAddRowData}
          defaultColDef={defaultColDef}
          totalPages={paymentVoucherData?.totalPage}
          onEdit={handleEditRow}
          onDelete={handleDeleteRow}
          onSave={handleSaveRow}
          onAfterActions={() => {
            setShowInlineAddRow("");
            setEditingId(null);
          }}
          getRowId={(params) => String(params.data.id)}
          onQueryChange={handleQueryChange}
          onRowClicked={handleRowClick}
          onGridReady={(event) => {
            gridApiRef.current = event.api;
          }}
        />
        {isLoading && (
          <LoadingState
            message="Đang đồng bộ dữ liệu"
            description="Hệ thống đang cập nhật danh sách phiếu chi, vui lòng chờ giây lát."
          />
        )}
      </div>
      <ConfirmDialog
        open={Boolean(confirmRow)}
        tone="danger"
        title="Xóa phiếu chi?"
        description={`Bạn chắc chắn muốn xóa phiếu chi "${confirmRow?.original_voucher_no ?? ""
          }"? Thao tác này không thể khôi phục.`}
        confirmLabel="Xóa"
        cancelLabel="Hủy"
        loading={deletePaymentVoucher.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmRow(null)}
      />
    </div>
  );
};

export default PaymentVouchers;
